import { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, TablePagination } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import { format } from 'date-fns';
import Highlighter from 'react-highlight-words';
import { format as timeagoFormat } from 'timeago.js';

import { getSearchResultV2, getSearchHistory } from 'src/apis/search';
import { getDashboardShowIds } from 'src/apis/dashboard';
import { IGarmScoreDto, IGarmScoreShowDetails } from 'src/types';
import { SearchResultProps, SearchHistoryProps } from 'src/types/search';
import useSidebar from 'src/hooks/useSidebar';
import { useTextStyles } from 'src/hooks/useTextStyles';
import ButtonDropdown from 'src/components/ButtonDropdown';
import { formatFilename } from 'src/utils';
import Button from 'src/components/NewButton';
import {
  downloadAsCsv,
  formatPodcastRating,
  getGarmDashboards,
} from 'src/utils';
import Spinner from 'src/components/Spinner';
import useAuth from 'src/hooks/useAuth';
import useDashboardCategories from 'src/hooks/useDashboardCategories';

import { MatchContent } from './MatchContent';
import SearchFilterSection from './SearchFilterSection';

const csvShowHeaders = [
  'id',
  'Show',
  'Host',
  'RSS Feed',
  'ImageURL',
  'collectionId',
  'Publisher',
  'Adult Sexual Explicit',
  'Arms & Ammunition',
  'Crime & Violation of Human Rights',
  'Death Injury or Military Conflict',
  'Online Piracy or Spam',
  'Hate Speech & Acts of Aggression',
  'bscenity & Profanity',
  'Illegal Drugs/Tobacco/e-Cigarettes/Vaping/Alcoho',
  'Terrorism',
  'Debated Sensitive Social Issues (Political issues)',
  'General Election',
  'US Elections',
  'Genres',
  'Listener',
  'Top IAB Categories',
  'Episode Count',
];

const csvEpisodeHeaders = [
  'Episode Title',
  'Guid',
  'Adult Sexual Explicit Data',
  'Adult Sexual Explicit',
  'Arms & Ammunition Data',
  'Arms & Ammunition',
  'Crime & Violation of Human Rights Data',
  'Crime & Violation of Human Rights',
  'Death Injury or Military Conflict Data',
  'Death Injury or Military Conflict',
  'Online Piracy or Spam Data',
  'Online Piracy or Spam',
  'Hate Speech & Acts of Aggression Data',
  'Hate Speech & Acts of Aggression',
  'Obscenity & Profanity Data',
  'Obscenity & Profanity',
  'Illegal Drugs/Tobacco/e-Cigarettes/Vaping/Alcohol Data',
  'Illegal Drugs/Tobacco/e-Cigarettes/Vaping/Alcohol',
  'Terrorism Data',
  'Terrorism',
  'Debated Sensitive Social Issues (Political issues) Data',
  'Debated Sensitive Social Issues (Political issues)',
  'GAMBLING Data',
  'GAMBLING',
  'OCCULT Data',
  'OCCULT',
  'NATURAL DISASTER Data',
  'NATURAL DISASTER',
  'General Election Data',
  'General Election',
  'US Election',
  'Audio File (AUTO)',
  'id',
  'Episode Link',
  'itunes stars',
  'Episode Date',
  'Show',
  'Episode Number',
  'Show Code',
  'IAB Data',
  'Matched Transcriptions',
];

const useStyles = makeStyles(() =>
  createStyles({
    layout: {
      display: 'flex',
      height: '100%',
    },
    container: {
      padding: 24,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      background: '#fff',
      height: '100%',
      overflow: 'auto',
      flex: 1,
    },
    title: {
      color: '#344054',
      fontSize: 24,
      fontWeight: 600,
      lineHeight: '32px',
    },
    options: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
    loading: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    },
    noContent: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
    },
    contentItem: {
      padding: 16,
      borderRadius: 8,
      border: '1px solid #D0D5DD',
      background: '#FCFCFD',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    },
    iabCategory: {
      padding: '2px 6px',
      borderRadius: 6,
      border: '1px solid #D0D5DD',
      background: '#FFF',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    },
    pagination: {
      paddingTop: 0,
      paddingBottom: 0,
      fontSize: 12,
      marginLeft: 'auto',
      border: '1px solid #D0D5DD',
      borderRadius: 12,
      padding: 0,

      '& .MuiTablePagination-toolbar': {
        minHeight: 'auto',
      },
    },
    fitlerOptionTitle: {
      fontWeight: 'bold',
      marginRight: 12,
    },
    iconButton: {
      padding: 0,
      width: 24,
      height: 24,
      marginLeft: 8,
    },
    historyItem: {
      padding: '8px 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      maxWidth: 350,
      width: '100%',
    },
  })
);

export interface OptionProps {
  label: string;
  value: string;
}

export interface FilterDateType {
  startDate?: Date;
  endDate?: Date;
}

const SEARCH_GROUPS = [
  { label: 'By Episode', value: 'episode' },
  { label: 'By Show', value: 'show' },
];

const Search = () => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const [result, setResult] = useState<Nullable<SearchResultProps>>(null);
  const [groupedShows, setGroupedShows] = useState<IGarmScoreShowDetails[]>([]);
  const [openShowIndex, setOpenShowIndex] = useState<number>(-1);
  const [group, setGroup] = useState<OptionProps>(SEARCH_GROUPS[0]);
  const [sortMode, setSortMode] = useState<string>('relevant');
  const [operatorSelect, setOperatorSelect] = useState<string>('AND');
  const [matchSelect, setMatchSelect] = useState<string>('partial');
  const [filterDateRange, setFilterDateRange] = useState<{
    startDate?: Date;
    endDate?: Date;
  }>({
    startDate: undefined,
    endDate: undefined,
  });
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [selectedDashboardId, setSelectedDashboardId] =
    useState<Nullable<string>>(null);
  const { setSecondSidebarOpen, setDashboardSidebar, setCampaignsSideBar } =
    useSidebar();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingShows, setLoadingShows] = useState<boolean>(false);
  const [fetchedData, setFetchedData] = useState<boolean>(false);
  const [selectedShows, setSelectedShows] = useState<string[] | undefined>(
    undefined
  );
  const [keyword, setKeyword] = useState<string>('');
  const [recentLoading, setRecentLoading] = useState<boolean>(false);
  const [histories, setHistories] = useState<SearchHistoryProps[]>([]);
  const { organization } = useAuth();

  const { filterMapInit } = useDashboardCategories();

  const searchWords = useMemo(() => {
    if (!keyword) {
      return [];
    }
    if (keyword[0] === '"' && keyword[keyword.length - 1] === '"') {
      return [keyword.substring(1, keyword.length - 1)];
    } else {
      const words = keyword.split(',');
      return matchSelect === 'exact'
        ? words
        : words.reduce((result: string[], item) => {
            return [...result, ...item.split(' ')];
          }, []);
    }
  }, [keyword, matchSelect]);

  const garmDashboards = useMemo(
    () => (organization ? getGarmDashboards(organization) : []),
    [organization]
  );

  const fetchHistories = useCallback(async () => {
    try {
      setRecentLoading(true);
      const { data } = await getSearchHistory();
      setRecentLoading(false);
      setHistories(data);
    } catch {
      setRecentLoading(false);
      setHistories([]);
    }
  }, []);

  const getShowsFromDashboard = useCallback(
    async (dashboardId: Nullable<string>) => {
      if (dashboardId) {
        setLoadingShows(true);
        try {
          const { showIds } = await getDashboardShowIds(dashboardId);
          setSelectedShows(showIds);
        } catch {
          setSelectedShows([]);
        } finally {
          setLoadingShows(false);
        }
      } else {
        setSelectedShows(undefined);
      }
    },
    [garmDashboards, filterMapInit]
  );

  const searchWithKeyword = useCallback(
    async (
      text: string,
      mode: string,
      operator: string,
      match: string,
      dateRange: {
        startDate?: Date;
        endDate?: Date;
      },
      page: number,
      pageSize: number,
      dataLevel: string
    ) => {
      if (loading) {
        return;
      }
      try {
        setLoading(true);
        setFetchedData(true);

        const { data } = await getSearchResultV2({
          page,
          pageSize,
          texts: text.split(', '),
          mode,
          showIds: selectedShows,
          operator,
          matchType: match,
          dateRange:
            dateRange.startDate && dateRange.endDate
              ? {
                  from: dateRange.startDate.toISOString(),
                  to: dateRange.endDate.toISOString(),
                }
              : undefined,
          dataLevel,
        });
        setLoading(false);
        setResult(data);
        const seedIds = new Set();
        const newGroupedShows = data.shows.filter((show) => {
          if (!seedIds.has(show.id)) {
            seedIds.add(show.id);
            return true;
          }
          return false;
        });
        setGroupedShows(newGroupedShows);
      } catch {
        setLoading(false);
        setResult(null);
      }
    },
    [selectedShows, group]
  );

  useEffect(() => {
    getShowsFromDashboard(selectedDashboardId);
  }, [selectedDashboardId]);

  useEffect(() => {
    setSecondSidebarOpen(false);
    setDashboardSidebar(false);
    setCampaignsSideBar(false);
    setKeyword('');
    fetchHistories();
  }, []);

  const addDefaultSrc = (ev: any) => {
    ev.target.src = '/images/placeholder.png';
  };

  const episodeView = (
    episode: IGarmScoreDto,
    show: Nullable<IGarmScoreShowDetails>,
    isShowGroup?: boolean
  ) => (
    <Box
      className={classes.contentItem}
      style={isShowGroup ? { border: 'none' } : {}}
    >
      <Box display='flex' gridGap={16} alignItems='center'>
        {!isShowGroup && (
          <img
            src={show?.logoUrl || formatFilename(show?.showOnDB)}
            alt={`${name}`}
            onError={addDefaultSrc}
            style={{ width: 60, borderRadius: 10 }}
          />
        )}
        <Box display='flex' gridGap={4} flexDirection='column'>
          <Box
            display='flex'
            gridGap={16}
            alignItems='center'
            flexDirection='row'
          >
            <Typography
              className={textClasses.lgBold}
              style={{ color: '#344054' }}
            >
              <Highlighter
                searchWords={searchWords}
                autoEscape={true}
                highlightStyle={{
                  background: '#FDE272',
                }}
                style={{
                  whiteSpace: 'pre-line',
                }}
                textToHighlight={episode['Episode Title'] || ''}
              />
            </Typography>
            <Typography
              className={textClasses.xsRegular}
              style={{ color: '#5EA1F2' }}
            >
              <Highlighter
                searchWords={searchWords}
                autoEscape={true}
                highlightStyle={{
                  background: '#FDE272',
                }}
                style={{
                  whiteSpace: 'pre-line',
                }}
                textToHighlight={show?.publisher || ''}
              />
            </Typography>
          </Box>
          <Box
            display='flex'
            gridGap={16}
            alignItems='center'
            flexDirection='row'
          >
            <Typography
              className={textClasses.xsRegular}
              style={{ color: '#98A2B3' }}
            >
              Aired{' '}
              {format(
                episode['Episode Date']
                  ? new Date(episode['Episode Date'])
                  : new Date(),
                'MM/dd/yyyy'
              )}
            </Typography>
            {show?.genreList && (
              <Typography
                className={textClasses.xsRegular}
                style={{ color: '#98A2B3' }}
              >
                Genres:{' '}
                <Highlighter
                  searchWords={searchWords}
                  autoEscape={true}
                  highlightStyle={{
                    background: '#FDE272',
                  }}
                  style={{
                    whiteSpace: 'pre-line',
                  }}
                  textToHighlight={show.genreList}
                />
              </Typography>
            )}
            {(episode['IAB New Data'] || episode['IAB Data']) && (
              <Box display='flex' gridGap={8} alignItems='center'>
                <Typography
                  className={textClasses.xsRegular}
                  style={{ color: '#98A2B3' }}
                >
                  Top IAB Categories:
                </Typography>
                {episode['IAB New Data']
                  ? Object.keys(episode['IAB New Data'])
                      .slice(0, 3)
                      .map((item) => (
                        <Box key={item} className={classes.iabCategory}>
                          <Typography
                            className={textClasses.xxsRegular}
                            style={{ color: '#344054' }}
                          >
                            <Highlighter
                              searchWords={searchWords}
                              autoEscape={true}
                              highlightStyle={{
                                background: '#FDE272',
                              }}
                              style={{
                                whiteSpace: 'pre-line',
                              }}
                              textToHighlight={item}
                            />
                          </Typography>
                        </Box>
                      ))
                  : Object.keys(episode['IAB Data'][0][0])
                      .slice(0, 3)
                      .map((item) => (
                        <Box key={item} className={classes.iabCategory}>
                          <Typography
                            className={textClasses.xxsRegular}
                            style={{ color: '#344054' }}
                          >
                            <Highlighter
                              searchWords={searchWords}
                              autoEscape={true}
                              highlightStyle={{
                                background: '#FDE272',
                              }}
                              style={{
                                whiteSpace: 'pre-line',
                              }}
                              textToHighlight={item}
                            />
                          </Typography>
                        </Box>
                      ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {!isShowGroup && keyword && !!episode.Matches?.length && (
        <Box marginTop='16px'>
          <MatchContent
            searchWords={searchWords}
            matches={episode.Matches || []}
          />
        </Box>
      )}
    </Box>
  );

  const pageLoading = loading || loadingShows;

  const noResult =
    !pageLoading && fetchedData && (!result || result.totalCount === 0);

  return (
    <Box className={classes.layout}>
      <SearchFilterSection
        dashboards={garmDashboards}
        selectedDashboardId={selectedDashboardId}
        setSelectedDashboardId={setSelectedDashboardId}
        handleSearch={searchWithKeyword}
        disableSearch={pageLoading}
        loadingShows={loadingShows}
        keyword={keyword}
        setKeyword={setKeyword}
        page={page}
        pageSize={pageSize}
        operatorSelect={operatorSelect}
        setOperatorSelect={setOperatorSelect}
        matchSelect={matchSelect}
        setMatchSelect={setMatchSelect}
        sortMode={sortMode}
        setSortMode={setSortMode}
        filterDateRange={filterDateRange}
        setFilterDateRange={setFilterDateRange}
        dataLevel={group.value}
      />
      <Box className={classes.container}>
        <Box
          display='flex'
          gridGap={4}
          flexDirection='row'
          justifyContent='space-between'
        >
          {fetchedData ? (
            <Typography className={classes.title}>
              {pageLoading || noResult
                ? ''
                : result &&
                  result.totalCount +
                    ' episodes and ' +
                    result.shows.length +
                    ' shows'}{' '}
              Search results for ‘
              <span style={{ color: '#5EA1F2' }}>{keyword}</span>’
            </Typography>
          ) : (
            ''
          )}
          <Button
            height={36}
            onClick={async () => {
              if (
                downloading ||
                !keyword ||
                !result ||
                !result.episodes.length
              ) {
                return;
              }
              if (result?.episodes && group.value === 'episode') {
                await downloadAsCsv({
                  data: result.episodes
                    .map((each) =>
                      [
                        each['Episode Title'],
                        each['Guid'],
                        JSON.stringify(each['GARM 1']),
                        each['GARM 1 Risk'],
                        JSON.stringify(each['GARM 2']),
                        each['GARM 2 Risk'],
                        JSON.stringify(each['GARM 3']),
                        each['GARM 3 Risk'],
                        JSON.stringify(each['GARM 4']),
                        each['GARM 4 Risk'],
                        JSON.stringify(each['GARM 5 & 9']),
                        each['GARM 5 & 9 Risk'],
                        JSON.stringify(each['GARM 6']),
                        each['GARM 6 Risk'],
                        JSON.stringify(each['GARM 7']),
                        each['GARM 7 Risk'],
                        JSON.stringify(each['GARM 8']),
                        each['GARM 8 Risk'],
                        JSON.stringify(each['GARM 10']),
                        each['GARM 10 Risk'],
                        JSON.stringify(each['GARM 11']),
                        each['GARM 11 Risk'],
                        JSON.stringify(each['GAMBLING']),
                        each['GAMBLING Risk'],
                        JSON.stringify(each['OCCULT']),
                        each['OCCULT Risk'],
                        JSON.stringify(each['NATURAL DISASTER']),
                        each['NATURAL DISASTER Risk'],
                        JSON.stringify(each['GENERAL ELECTIONS']),
                        each['GENERAL ELECTIONS Risk'],
                        JSON.stringify({
                          overall: (each['ELECTIONS PERCENTAGE'] || 0) + '%',
                          sources: Object.keys(
                            each['POLITICIAN SCORES'] || {}
                          ).map((key) => ({
                            name: key,
                            count: (each['POLITICIAN SCORES'] || {})[key]
                              ?.count,
                            sentiment: (each['POLITICIAN SCORES'] || {})[key]
                              ?.sentiment,
                          })),
                          utterances: {
                            '+Democrat': each['DEMOCRAT POSITIVE'] || 0,
                            '=Democrat': each['DEMOCRAT NEUTRAL'] || 0,
                            '-Democrat': each['DEMOCRAT NEGATIVE'] || 0,
                            '+Republican': each['REPUBLICAN POSITIVE'] || 0,
                            '=Republican': each['REPUBLICAN NEUTRAL'] || 0,
                            '-Republican': each['REPUBLICAN NEGATIVE'] || 0,
                          },
                        }),
                        each['Audio File (AUTO)'],
                        each.id,
                        each['Episode Link'],
                        formatPodcastRating(each['Listeners'] || ''),
                        each['Episode Date'] &&
                          format(new Date(each['Episode Date']), 'MM/dd/yyyy'),
                        each.Show,
                        each['Episode Number'],
                        each['Show Code'],
                        each['IAB New Data']
                          ? Object.keys(each['IAB Data']).join(', ')
                          : each['IAB Data']
                          ? Object.keys(each['IAB Data'][0][0]).join(', ')
                          : '',
                        each.Matches?.length,
                      ]
                        .map((value) => {
                          if (!value) return value;
                          let result = String(value).replace(/"/g, '""');
                          if (result.search(/("|,|\n)/g) >= 0)
                            result = '"' + result + '"';
                          return result;
                        })
                        .join(',')
                    )
                    .join('\n'),
                  headers: csvEpisodeHeaders,
                  fileName: 'episode_search_export',
                });
              }

              if (result?.shows && group.value === 'show') {
                await downloadAsCsv({
                  data: result.shows
                    .map((each: any) =>
                      [
                        each.id,
                        each.show,
                        each.hostList,
                        each.rssFeedUrl
                          ? each.rssFeedUrl.replaceAll('\r', '')
                          : '',
                        each.logoUrl,
                        each.collectionId,
                        each.publisher,
                        each.riskHistories?.current?.garm1Risk?.averageRisk,
                        each.riskHistories?.current?.garm2Risk?.averageRisk,
                        each.riskHistories?.current?.garm3Risk?.averageRisk,
                        each.riskHistories?.current?.garm4Risk?.averageRisk,
                        each.riskHistories?.current?.garm5Risk?.averageRisk,
                        each.riskHistories?.current?.garm6Risk?.averageRisk,
                        each.riskHistories?.current?.garm7Risk?.averageRisk,
                        each.riskHistories?.current?.garm8Risk?.averageRisk,
                        each.riskHistories?.current?.garm10Risk?.averageRisk,
                        each.riskHistories?.current?.garm11Risk?.averageRisk,
                        each.riskHistories?.current?.generalElectionsRisk
                          ?.averageRisk,
                        (each.electionsPercentage?.medianRiskValue || 0) + '%',
                        each.genreList,
                        each.listenerCount,
                        each.iabCategories
                          ? Object.keys(each.iabCategories).join(', ')
                          : '',
                        each.totalEpisodeCount,
                      ]
                        .map((value) => {
                          if (!value) return value;
                          let result = String(value).replace(/"/g, '""');
                          if (result.search(/("|,|\n)/g) >= 0)
                            result = '"' + result + '"';
                          return result;
                        })
                        .join(',')
                    )
                    .join('\n'),
                  headers: csvShowHeaders,
                  fileName: 'show_search_export',
                });
              }
            }}
            style={{ marginLeft: 'auto' }}
            width={'auto'}
          >
            {!downloading ? (
              group.value === 'episode' ? (
                <span>Export Episodes</span>
              ) : (
                <span>Export Show List</span>
              )
            ) : (
              <Spinner color='white' size={20} />
            )}
          </Button>
        </Box>
        {!fetchedData ? (
          <Box className={classes.noContent}>
            {recentLoading && (
              <Box className={classes.loading}>
                <CircularProgress />
              </Box>
            )}
            <Typography
              className={textClasses.xlBold}
              style={{ color: '#101828', marginTop: 24 }}
            >
              Recent
            </Typography>
            {histories.map((item, index) => (
              <Box
                key={index}
                className={classes.historyItem}
                onClick={() => {
                  const text = item.texts?.join(', ') || item.query;
                  setKeyword(text);
                  text &&
                    searchWithKeyword(
                      text,
                      sortMode,
                      operatorSelect,
                      matchSelect,
                      filterDateRange,
                      page,
                      pageSize,
                      group.value
                    );
                }}
              >
                <Typography
                  className={textClasses.smMedium}
                  style={{ color: '#101828' }}
                >
                  {item.texts ? item.texts.join(', ') : item.query}
                </Typography>
                <Typography
                  className={textClasses.smRegular}
                  style={{ color: '#98A2B3' }}
                >
                  {timeagoFormat(new Date(item.date || ''))}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : noResult ? (
          <Box className={classes.noContent}>
            <img
              src={'/images/search/noResultLogo.png'}
              width={200}
              height={160}
              alt='no result logo'
            />
            <Typography
              className={textClasses.xlBold}
              style={{ color: '#101828', marginTop: 24 }}
            >
              No results found
            </Typography>
            <Typography
              className={textClasses.baseRegular}
              style={{
                color: '#475467',
                marginTop: 8,
                textAlign: 'center',
                maxWidth: 352,
              }}
            >
              Your search “{keyword}“ did not find any results. Make sure your
              search term is properly spelled. Please try your search again with
              a different keyword.
            </Typography>
          </Box>
        ) : (
          <>
            <Box className={classes.options}>
              <ButtonDropdown
                id='search-group'
                selectedOption={group}
                options={SEARCH_GROUPS}
                handleSelect={(option) => {
                  setGroup(option);
                }}
              />
              {group.value === 'episode' && (
                <TablePagination
                  rowsPerPageOptions={[10, 20, 30]}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onPageChange={(_, newPage) => {
                    setPage(newPage);
                    searchWithKeyword(
                      keyword,
                      sortMode,
                      operatorSelect,
                      matchSelect,
                      filterDateRange,
                      newPage,
                      pageSize,
                      group.value
                    );
                  }}
                  onRowsPerPageChange={(e) => {
                    const newPageSize = parseInt(e.target.value, 10);
                    setPageSize(newPageSize);
                    setPage(0);
                    searchWithKeyword(
                      keyword,
                      sortMode,
                      operatorSelect,
                      matchSelect,
                      filterDateRange,
                      0,
                      newPageSize,
                      group.value
                    );
                  }}
                  count={result?.totalCount || 0}
                  page={page}
                  rowsPerPage={pageSize}
                  className={classes.pagination}
                  nextIconButtonProps={{
                    size: 'small',
                  }}
                  backIconButtonProps={{
                    size: 'small',
                  }}
                />
              )}
            </Box>
            {loading ? (
              <Box className={classes.loading}>
                <CircularProgress />
                <Typography
                  className={textClasses.smRegular}
                  style={{ marginTop: 12 }}
                >
                  Searching transcripts for mentions… this may take a moment.
                </Typography>
              </Box>
            ) : (
              <>
                {group.value === 'episode' ? (
                  <Box className={classes.content}>
                    {result?.episodes.map((episode) => {
                      const show = result.shows.filter(
                        (item) => item.show === episode.Show
                      )[0];
                      return (
                        <Box key={episode.id}>{episodeView(episode, show)}</Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box className={classes.content}>
                    {result?.shows.slice(0, 100).map((show, showIndex) => (
                      <Box key={show.id}>
                        <Box
                          className={classes.contentItem}
                          style={
                            showIndex === openShowIndex
                              ? {
                                  borderRadius: '8px 8px 0 8px',
                                }
                              : {}
                          }
                        >
                          <Box display='flex' gridGap={16} alignItems='center'>
                            <img
                              src={
                                show.logoUrl || formatFilename(show.showOnDB)
                              }
                              alt={`${name}`}
                              onError={addDefaultSrc}
                              style={{ width: 60, borderRadius: 10 }}
                            />
                            <Box
                              display='flex'
                              flex={1}
                              gridGap={4}
                              flexDirection='column'
                            >
                              <Box
                                display='flex'
                                gridGap={16}
                                alignItems='center'
                                flexDirection='row'
                              >
                                <Typography
                                  className={textClasses.lgBold}
                                  style={{ color: '#344054' }}
                                >
                                  <Highlighter
                                    searchWords={searchWords}
                                    autoEscape={true}
                                    highlightStyle={{
                                      background: '#FDE272',
                                    }}
                                    style={{
                                      whiteSpace: 'pre-line',
                                    }}
                                    textToHighlight={show.show}
                                  />
                                </Typography>
                                <Typography
                                  className={textClasses.xsRegular}
                                  style={{ color: '#5EA1F2' }}
                                >
                                  <Highlighter
                                    searchWords={searchWords}
                                    autoEscape={true}
                                    highlightStyle={{
                                      background: '#FDE272',
                                    }}
                                    style={{
                                      whiteSpace: 'pre-line',
                                    }}
                                    textToHighlight={show.publisher || ''}
                                  />
                                </Typography>
                                {/* <Button
                                  color='secondary'
                                  disableElevation
                                  variant='text'
                                  size='small'
                                  height={34}
                                  onClick={() => {
                                    if (openShowIndex === showIndex) {
                                      setOpenShowIndex(-1);
                                    } else {
                                      setOpenShowIndex(showIndex);
                                    }
                                  }}
                                  style={{ marginLeft: 'auto' }}
                                  // className={classes.button}
                                >
                                  <ArrowUpIcon
                                    width={16}
                                    height={16}
                                    stroke='#344054'
                                    cursor='pointer'
                                    style={
                                      openShowIndex === showIndex
                                        ? {}
                                        : { transform: 'rotate(180deg)' }
                                    }
                                  />
                                  <Typography
                                    className={textClasses.xsBold}
                                    style={{
                                      color: '#344054',
                                    }}
                                  >
                                    Show Episodes
                                  </Typography>
                                </Button> */}
                              </Box>
                              <Box
                                display='flex'
                                gridGap={16}
                                alignItems='center'
                                flexDirection='row'
                              >
                                <Typography
                                  className={textClasses.xsRegular}
                                  style={{ color: '#98A2B3' }}
                                >
                                  {show.listenerCount || 'No'} Listeners
                                </Typography>
                                <Typography
                                  className={textClasses.xsRegular}
                                  style={{ color: '#98A2B3' }}
                                >
                                  {show.totalEpisodeCount} Episodes
                                </Typography>
                                {show.genreList && (
                                  <Typography
                                    className={textClasses.xsRegular}
                                    style={{ color: '#98A2B3' }}
                                  >
                                    Genres:{' '}
                                    <Highlighter
                                      searchWords={searchWords}
                                      autoEscape={true}
                                      highlightStyle={{
                                        background: '#FDE272',
                                      }}
                                      style={{
                                        whiteSpace: 'pre-line',
                                      }}
                                      textToHighlight={show.genreList}
                                    />
                                  </Typography>
                                )}
                                {show.iabCategories && (
                                  <Box
                                    display='flex'
                                    gridGap={8}
                                    alignItems='center'
                                  >
                                    <Typography
                                      className={textClasses.xsRegular}
                                      style={{ color: '#98A2B3' }}
                                    >
                                      Top IAB Categories:
                                    </Typography>
                                    {Object.keys(show.iabCategories)
                                      .slice(0, 3)
                                      .map((item) => (
                                        <Box
                                          key={item}
                                          className={classes.iabCategory}
                                        >
                                          <Typography
                                            className={textClasses.xxsRegular}
                                            style={{ color: '#344054' }}
                                          >
                                            <Highlighter
                                              searchWords={searchWords}
                                              autoEscape={true}
                                              highlightStyle={{
                                                background: '#FDE272',
                                              }}
                                              style={{
                                                whiteSpace: 'pre-line',
                                              }}
                                              textToHighlight={item}
                                            />
                                          </Typography>
                                        </Box>
                                      ))}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        {/* {showIndex === openShowIndex && (
                          <Box
                            className={classes.contentItem}
                            style={{
                              marginLeft: 32,
                              padding: 0,
                              borderRadius: '0 0 8px 8px',
                            }}
                          >
                            {result?.episodes
                              .filter((episode) => episode.Show === show.show)
                              .map((episode) =>
                                episodeView(episode, show, true)
                              )}
                          </Box>
                        )} */}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Search;
